/**
 * TagFilter Component
 *
 * Checkbox group for filtering care logs by predefined tags.
 */

import { Tag, X } from 'lucide-react';
import { CARE_LOG_TAGS, CareLogTag } from './CareLogForm';

interface TagFilterProps {
  selectedTags: CareLogTag[];
  onTagsChange: (tags: CareLogTag[]) => void;
  onClear?: () => void;
}

const TAG_COLORS: Record<string, { bg: string; border: string; text: string }> = {
  medical: { bg: 'bg-red-50', border: 'border-red-300', text: 'text-red-700' },
  school: { bg: 'bg-blue-50', border: 'border-blue-300', text: 'text-blue-700' },
  insurance: { bg: 'bg-green-50', border: 'border-green-300', text: 'text-green-700' },
  admin: { bg: 'bg-gray-50', border: 'border-gray-300', text: 'text-gray-700' },
  behavior: { bg: 'bg-purple-50', border: 'border-purple-300', text: 'text-purple-700' },
  medication: { bg: 'bg-orange-50', border: 'border-orange-300', text: 'text-orange-700' },
};

export function TagFilter({
  selectedTags,
  onTagsChange,
  onClear,
}: TagFilterProps): JSX.Element {
  const handleTagToggle = (tag: CareLogTag) => {
    if (selectedTags.includes(tag)) {
      onTagsChange(selectedTags.filter((t) => t !== tag));
    } else {
      onTagsChange([...selectedTags, tag]);
    }
  };

  const hasFilters = selectedTags.length > 0;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="label text-xs flex items-center gap-1 mb-0">
          <Tag size={12} />
          Filter by Tag
        </label>
        {hasFilters && onClear && (
          <button
            onClick={onClear}
            className="btn-ghost btn-xs flex items-center gap-1 text-muted hover:text-ink"
            title="Clear tag filters"
          >
            <X size={12} />
            Clear
          </button>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        {CARE_LOG_TAGS.map((tag) => {
          const isSelected = selectedTags.includes(tag.value);
          const colors = TAG_COLORS[tag.value] || { bg: 'bg-gray-50', border: 'border-gray-300', text: 'text-gray-700' };

          return (
            <label
              key={tag.value}
              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full border cursor-pointer transition-all text-sm ${
                isSelected
                  ? `${colors.bg} ${colors.border} ${colors.text} ring-1 ring-offset-1 ring-current`
                  : 'bg-white border-soft-neutral text-muted hover:border-ink'
              }`}
            >
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => handleTagToggle(tag.value)}
                className="sr-only"
              />
              {tag.label}
            </label>
          );
        })}
      </div>
    </div>
  );
}

export default TagFilter;
